import { createSelector } from 'reselect';
import { segmentSelectorFactory, hasErrorObject, getDynpackDatesErrorSelector } from './selectors';
import { HotelRoomUtils } from '../../utils';
import { HotelStandalonePaxRestrictionPropsDefault, ErrorType } from '../../models';

const getSegmentDates = segmentSelectorFactory('date');
const getSegmentDestinations = segmentSelectorFactory('destination');
const validateNotEmpty = ({ type, value, index }) => !value ? { type, error: 'empty', index } : null;
const getDateError = (date, index) => validateNotEmpty({ type: ErrorType.DATE, value: date, index });
const getDestinationError = (destination, index) => validateNotEmpty({ type: ErrorType.ARRIVAL, value: destination, index });

export const getRooms = ({ currentSearch }) =>
    HotelRoomUtils.getRoomsAvailableByRestrictions(currentSearch.rooms, HotelStandalonePaxRestrictionPropsDefault);

export const canAddRoom = createSelector(
    [getRooms],
    (rooms) => HotelRoomUtils.hasAvailableRoomLimitation(rooms, HotelStandalonePaxRestrictionPropsDefault)
);

export const getHotelStandaloneAdultsAndKids = createSelector(
    [getRooms],
    (rooms) => HotelRoomUtils.getTotalAdultsAndKids(rooms, HotelStandalonePaxRestrictionPropsDefault),
);

const getDestinationErrorsSelector = createSelector(
    [getSegmentDestinations],
    (segmentDestinations) => Object.keys(segmentDestinations)
        .filter((segmentKey, index) => index === HotelStandalonePaxRestrictionPropsDefault.hotelSegmentIndex)
        .reduce((errorResult, segmentKey, index) => {
            const error = getDestinationError(segmentDestinations[segmentKey], index);
            if (error) errorResult[segmentKey] = error;
            return errorResult;
        }, {}),
);

const getDateErrorsSelector = createSelector(
    [getSegmentDates, getDynpackDatesErrorSelector],
    (segmentDates, dynpackDatesError) => Object.keys(segmentDates)
        .reduce((errorResult, segmentKey, index) => {
            let error = getDateError(segmentDates[segmentKey], index);
            if (error) {
                errorResult[segmentKey] = error;
            }

            if (index === 1 && !error) {
                errorResult[segmentKey] = dynpackDatesError;
            }

            return errorResult;
        }, {}),
);


const getSegmentsErrorsSelector = createSelector(
    [getDateErrorsSelector, getDestinationErrorsSelector],
    (datesErrors, destinationsErrors) => {
        return []
            .concat(Object.values(datesErrors))
            .concat(Object.values(destinationsErrors));
    },
);

const getAgeKidsErrorSelector = createSelector([getRooms], HotelRoomUtils.getAgeKidsValidation.bind(HotelRoomUtils));

export const hasHotelStandaloneKidsErrors = createSelector(
    [getAgeKidsErrorSelector],
    hasErrorObject
);

export const getHotelStandaloneValidationErrorsSelector = createSelector(
    [getSegmentsErrorsSelector, getAgeKidsErrorSelector],
    (segmentErrors, ageKidsErrors) => {
        return []
            .concat(segmentErrors)
            .concat(ageKidsErrors)
            .filter(hasErrorObject);
    },
);

export const isHotelStandaloneValidSearch = createSelector(
    [getHotelStandaloneValidationErrorsSelector],
    (validationErrors) => !validationErrors.length,
);
